// ============================================================
// BOOKING FILTERS BAR COMPONENT
// ============================================================

import { Filter, Search, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import {
  STATUS_OPTIONS,
  TYPE_OPTIONS,
  DATE_OPTIONS,
  PAYMENT_OPTIONS,
} from '../../constants/booking.constants';

interface BookingFiltersBarProps {
  searchQuery: string;
  statusFilter: string;
  typeFilter: string;
  dateFilter: string;
  paymentFilter: string;
  totalResults: number;
  filteredResults: number;
  hasActiveFilters: boolean;
  onSearchChange: (query: string) => void;
  onFilterChange: (key: string, value: string) => void;
  onResetFilters: () => void;
}

export function BookingFiltersBar({
  searchQuery,
  statusFilter,
  typeFilter,
  dateFilter,
  paymentFilter,
  totalResults,
  filteredResults,
  hasActiveFilters,
  onSearchChange,
  onFilterChange,
  onResetFilters,
}: BookingFiltersBarProps) {
  const isFilterActive = (filterValue: string) => filterValue !== 'all';

  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-3">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full">
        <div className="flex items-center gap-2 text-slate-400 shrink-0 border-r border-slate-200 pr-3">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-semibold text-slate-700">Filtres</span>
        </div>

        <SearchInput value={searchQuery} onChange={onSearchChange} />

        <FilterSelect
          value={statusFilter}
          options={STATUS_OPTIONS}
          isActive={isFilterActive(statusFilter)}
          onChange={(value) => onFilterChange('status', value)}
        />

        <FilterSelect
          value={typeFilter}
          options={TYPE_OPTIONS}
          isActive={isFilterActive(typeFilter)}
          onChange={(value) => onFilterChange('type', value)}
        />

        <FilterSelect
          value={dateFilter}
          options={DATE_OPTIONS}
          isActive={isFilterActive(dateFilter)}
          onChange={(value) => onFilterChange('date', value)}
        />

        <FilterSelect
          value={paymentFilter}
          options={PAYMENT_OPTIONS}
          isActive={isFilterActive(paymentFilter)}
          onChange={(value) => onFilterChange('payment', value)}
        />

        {hasActiveFilters && (
          <button
            onClick={onResetFilters}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 px-2 py-1 shrink-0 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Réinitialiser
          </button>
        )}

        <ResultsCount total={totalResults} filtered={filteredResults} />
      </div>
    </div>
  );
}

function SearchInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="flex-1 min-w-0 flex items-center gap-2 px-3 bg-slate-50 border border-slate-200 rounded-md cursor-text focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/30 transition-all">
      <Search className="w-4 h-4 text-slate-400 shrink-0" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-grow min-w-0 py-1.5 bg-transparent border-none outline-none text-sm text-slate-900 placeholder:text-slate-400"
        placeholder="Référence, client, espace…"
      />
    </label>
  );
}

interface FilterSelectProps {
  value: string;
  options: readonly { value: string; label: string }[];
  isActive: boolean;
  onChange: (value: string) => void;
}

function FilterSelect({ value, options, isActive, onChange }: FilterSelectProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={cn(
          'w-auto min-w-[130px] shrink-0 bg-white h-auto py-1.5 focus:ring-primary/20',
          isActive
            ? 'border-primary bg-primary/5 text-primary font-medium'
            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ResultsCount({ total, filtered }: { total: number; filtered: number }) {
  const displayText = filtered === total ? `${total} résultats` : `${filtered} sur ${total}`;

  return (
    <span className="shrink-0 whitespace-nowrap text-sm text-slate-500 font-medium ml-auto pl-3 border-l border-slate-200">
      {displayText}
    </span>
  );
}
